import { useEffect, useRef, useCallback } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectSyncStatus,
  selectIsSyncing,
  setWallets,
  setSyncPending,
  setSyncFailed,
  type EmbeddedWalletSyncStatus,
} from "@/store/slices/embeddedWalletSlice";
import { selectIsAuthenticated } from "@/store/slices/authSlice";
import { useWeb3AuthGateway } from "@/adapters/gateways/Web3AuthGateway";

/**
 * Return type for useEmbeddedWalletSync hook
 */
export interface UseEmbeddedWalletSyncResult {
  /** Current sync status: 'idle' | 'syncing' | 'synced' | 'error' */
  syncStatus: EmbeddedWalletSyncStatus;
  /** Whether sync is currently in progress */
  isSyncing: boolean;
  /** Manually trigger a sync (useful for retry) */
  triggerSync: () => void;
}

/**
 * Hook to manage embedded wallet synchronization
 *
 * Automatically triggers Web3Auth wallet sync after successful Cognito login.
 * Sync runs as a non-blocking background operation (fire and forget).
 *
 * ## Behavior
 * - Watches `isAuthenticated` state changes
 * - Triggers sync when user becomes authenticated AND sync is idle
 * - Does NOT block app initialization or usage
 * - Errors are logged but do NOT show to user
 *
 * ## Usage
 * Place this hook in a component that renders after authentication,
 * typically in App.tsx or a protected route wrapper.
 *
 * @example
 * ```tsx
 * function App() {
 *   // Sync runs automatically when user logs in
 *   const { syncStatus, isSyncing } = useEmbeddedWalletSync();
 *
 *   // Optional: Show subtle indicator (but never errors)
 *   // if (isSyncing) console.log('Syncing wallets...');
 *
 *   return <MyApp />;
 * }
 * ```
 *
 * @returns {UseEmbeddedWalletSyncResult} Object with sync status and manual trigger
 *
 * @remarks
 * AC 4: Web3Auth login does NOT block app usage - runs asynchronously
 * AC 10: Sync runs as non-blocking background operation
 * AC 13: Web3Auth failures logged but do NOT block app
 * AC 14: Users can continue using app even if sync fails
 * AC 15: Errors logged but NOT shown to users
 */
export function useEmbeddedWalletSync(): UseEmbeddedWalletSyncResult {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const syncStatus = useAppSelector(selectSyncStatus);
  const isSyncing = useAppSelector(selectIsSyncing);
  const gateway = useWeb3AuthGateway();

  // Track if we've already triggered sync for this auth session
  const hasSyncedRef = useRef(false);

  /**
   * Get Cognito ID token and sync wallets using Web3Auth gateway
   *
   * This function is non-blocking - it runs in background
   * and dispatches actions to update state.
   * Errors are caught and logged but never thrown or shown to users.
   */
  const performSync = useCallback(async () => {
    // Skip if gateway is not available (Web3Auth disabled)
    if (!gateway.isAvailable()) {
      console.log("[useEmbeddedWalletSync] Web3Auth not available, skipping sync");
      return;
    }

    try {
      // Get current Amplify session to extract ID token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.warn("[useEmbeddedWalletSync] No ID token available");
        return;
      }

      // Set syncing state
      dispatch(setSyncPending());

      // Authenticate with Web3Auth using Cognito token
      await gateway.authenticateWithCognito(idToken);

      // Get embedded wallets from Web3Auth
      const wallets = await gateway.getEmbeddedWallets();

      // Update state with wallets
      dispatch(setWallets(wallets));

      console.log("[useEmbeddedWalletSync] Wallets synced:", wallets.length);
    } catch (error) {
      // Log but don't throw - this should never block the app (AC: 13, 14, 15)
      console.error("[useEmbeddedWalletSync] Sync failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      dispatch(setSyncFailed(errorMessage));
    }
  }, [dispatch, gateway]);

  /**
   * Manual sync trigger (for retry or explicit sync)
   *
   * Use this when:
   * - User explicitly requests a wallet refresh
   * - Implementing a retry mechanism
   * - Need to re-sync after status was reset
   */
  const triggerSync = useCallback(() => {
    if (isSyncing) {
      console.log("[useEmbeddedWalletSync] Sync already in progress");
      return;
    }
    hasSyncedRef.current = false;
    performSync();
    hasSyncedRef.current = true;
  }, [isSyncing, performSync]);

  /**
   * Watch auth state and trigger sync when user becomes authenticated
   *
   * Sync triggers when ALL conditions are met:
   * 1. User is authenticated (Cognito login successful)
   * 2. Sync is idle (not already syncing or synced)
   * 3. Haven't already triggered sync for this session (prevents double-sync)
   */
  useEffect(() => {
    // Only sync if:
    // 1. User is authenticated
    // 2. Sync is idle (not already syncing or synced)
    // 3. Haven't already triggered sync for this session
    if (isAuthenticated && syncStatus === "idle" && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      performSync();
    }

    // Reset sync flag when user logs out
    if (!isAuthenticated) {
      hasSyncedRef.current = false;
    }
  }, [isAuthenticated, syncStatus, performSync]);

  return {
    syncStatus,
    isSyncing,
    triggerSync,
  };
}
